/**
 * Anonymous signal cookie. Captures lightweight interest signals from
 * guests (no auth required) so the jobs page can personalize the very
 * first session, not just the second one after sign-in.
 *
 * Storage shape: a comma-separated list of recently-viewed category
 * slugs, capped at MAX_CATEGORIES, in reverse-chronological order
 * (newest first). The cookie is small (well under 500 bytes), easy to
 * inspect, and deletable in one round trip — no server-side state.
 *
 * The cookie is read server-side from RSC pages via `next/headers`'s
 * `cookies()` helper and written via the same. We do NOT mirror it to
 * a Set-Cookie on every fetch — only on category page loads.
 *
 * A second cookie, `ld_filters_v1`, stores the authenticated user's
 * last-applied job-type and remote-type filter preferences so they are
 * restored across sessions without requiring the user to re-select them.
 * It is also fed into the relevance scorer as soft signals for anonymous
 * visitors who have used the filter UI before signing in.
 */

import { dedupeFoldCase } from "@/lib/match/skills";
import type { UserSignals } from "@/lib/personalization/score";

// Cookie NAME constants live in their own dependency-free module so
// `src/lib/cache/edge-html-cache.ts` (hot request path) can import them
// directly without pulling in this file's `@/lib/match/skills` dependency —
// see `signal-cookie-names.ts` for the full rationale. Re-exported here so
// every existing importer of these names from this module keeps working.
export {
	ANON_SIGNAL_COOKIE,
	ANONYMOUS_SIGNAL_COOKIE_NAMES,
	BANNER_DISMISSED_COOKIE,
	FILTER_SIGNAL_COOKIE,
	GUEST_LAST_SEEN_COOKIE,
	HUNT_INTEREST_COOKIE,
	RECENT_JOBS_COOKIE,
	SEARCH_NUDGE_DISMISSED_COOKIE,
} from "@/lib/personalization/signal-cookie-names";

export const MAX_CATEGORIES = 12;
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90; // 90 days

const SLUG_PATTERN = /^[a-z][a-z0-9-]{0,40}$/;

const VALID_JOB_TYPES = new Set([
	"full-time",
	"part-time",
	"contract",
	"freelance",
]);
const VALID_REMOTE_TYPES = new Set(["worldwide", "regional", "timezone-based"]);

export type AnonymousSignals = {
	categorySlugs: string[];
};

/** Sticky filter preferences captured from authenticated filter usage. */
export type FilterSignals = {
	jobType: string | null;
	remoteType: string | null;
};

/** Parses the cookie value into a deduplicated, validated slug list. */
export function parseAnonymousSignalCookie(
	raw: string | null | undefined,
): AnonymousSignals {
	if (!raw) return { categorySlugs: [] };
	const parts = raw
		.split(",")
		.map((s) => s.trim())
		.filter((s) => SLUG_PATTERN.test(s));
	// Dedup while preserving first-seen order.
	const seen = new Set<string>();
	const ordered: string[] = [];
	for (const p of parts) {
		if (!seen.has(p)) {
			seen.add(p);
			ordered.push(p);
		}
		if (ordered.length >= MAX_CATEGORIES) break;
	}
	return { categorySlugs: ordered };
}

/**
 * Prepends a new category slug onto the existing list (newest first)
 * and serializes back to a cookie value. Returns null if `nextSlug`
 * fails validation — caller should leave the cookie unchanged.
 */
export function pushCategorySlug(
	currentRaw: string | null | undefined,
	nextSlug: string,
): string | null {
	if (!SLUG_PATTERN.test(nextSlug)) return null;
	const current = parseAnonymousSignalCookie(currentRaw);
	const next = [
		nextSlug,
		...current.categorySlugs.filter((s) => s !== nextSlug),
	].slice(0, MAX_CATEGORIES);
	return next.join(",");
}

/**
 * Parses the `ld_filters_v1` cookie into validated jobType / remoteType
 * signals. Invalid or absent values are returned as null so callers can
 * safely skip them without special-casing undefined.
 */
export function parseFilterSignalCookie(
	raw: string | null | undefined,
): FilterSignals {
	if (!raw) return { jobType: null, remoteType: null };
	try {
		const parsed = JSON.parse(raw);
		if (
			typeof parsed !== "object" ||
			parsed === null ||
			Array.isArray(parsed)
		) {
			return { jobType: null, remoteType: null };
		}
		const jobType =
			typeof parsed.type === "string" && VALID_JOB_TYPES.has(parsed.type)
				? parsed.type
				: null;
		const remoteType =
			typeof parsed.remote === "string" && VALID_REMOTE_TYPES.has(parsed.remote)
				? parsed.remote
				: null;
		return { jobType, remoteType };
	} catch {
		return { jobType: null, remoteType: null };
	}
}

/** Serializes filter signals to a JSON string suitable for the cookie value. */
export function serializeFilterSignals(signals: FilterSignals): string {
	const obj: Record<string, string> = {};
	if (signals.jobType) obj.type = signals.jobType;
	if (signals.remoteType) obj.remote = signals.remoteType;
	return JSON.stringify(obj);
}

/**
 * Converts the anonymous cookie payload into the same `UserSignals`
 * shape `hasPersonalizationSignals` and the scorer consume. Category
 * slugs map straight onto `workInterests` — the scorer already
 * resolves interests through `INTEREST_TO_CATEGORY`, and category
 * slugs are valid interest values for that map (the keys overlap).
 *
 * When filter signals are also provided (from `ld_filters_v1`) they are
 * included as `preferredJobTypes` / `preferredRemoteTypes` so the
 * personalization scorer can apply a small ranking boost for matching
 * job types even before the user completes their profile.
 *
 * When a hunt-interest aggregate is provided (from `hunt_interest_v1`, the
 * rolling list of recently-viewed jobs) its skills and industries seed
 * `extractedSkills` / `industries`, and its remote preference back-fills
 * `preferredRemoteTypes` when no explicit filter remote is set. This carries a
 * guest's job-detail browsing into the feed ranking, not just the Hunt greeting.
 */
export function toUserSignals(
	anon: AnonymousSignals,
	filters?: FilterSignals,
	hunt?: HuntInterestAggregate,
): UserSignals {
	return {
		workInterests: anon.categorySlugs,
		preferredRoles: [],
		extractedSkills: hunt?.skills ?? [],
		industries: hunt?.industries ?? [],
		yearsExperience: null,
		preferredJobTypes: filters?.jobType ? [filters.jobType] : [],
		preferredRemoteTypes: filters?.remoteType
			? [filters.remoteType]
			: hunt?.remoteType
				? [hunt.remoteType]
				: [],
	};
}

/** Cookie attributes for the Set-Cookie response header. */
export const ANON_SIGNAL_COOKIE_OPTIONS = {
	maxAge: COOKIE_MAX_AGE_SECONDS,
	path: "/",
	sameSite: "lax" as const,
	httpOnly: false, // intentional — surface to client analytics if needed
	secure: true,
} as const;

/** Cookie attributes for the sticky filter signal cookie. */
export const FILTER_SIGNAL_COOKIE_OPTIONS = {
	maxAge: COOKIE_MAX_AGE_SECONDS,
	path: "/",
	sameSite: "lax" as const,
	httpOnly: false,
	secure: true,
} as const;

// ---------------------------------------------------------------------------
// Hunt interest cookie — written client-side when a user views a job detail
// page, read by startHuntSession to warm-start cold users.
// ---------------------------------------------------------------------------

export const HUNT_INTEREST_MAX_AGE_SECONDS = 60 * 60 * 24 * 7; // 7 days

/** How recently the browsed-job cookie must have been written for Hunt to claim
 * "I saw you browsing" in its greeting. The seed still warms the card for older
 * cookies — only the *inferred* greeting (which asserts a just-now view) is
 * gated, so a week-old cookie no longer tells a returning user they were "just"
 * browsing. Cookies written before the `ts` field shipped have no timestamp and
 * are treated as stale. */
export const HUNT_INTEREST_FRESH_MS = 60 * 60 * 1000; // 1 hour

export type HuntInterestSignal = {
	/** Version guard so future schema changes don't parse stale cookies incorrectly. */
	v: 1;
	skills: string[];
	remote: string | null;
	industry: string | null;
	/** Epoch ms when the cookie was written (the job-detail view time). Optional:
	 * cookies written before this field shipped omit it and read as stale. */
	ts?: number;
};

const VALID_HUNT_REMOTE = new Set(["worldwide", "regional", "onsite-okay"]);
// remote values on jobs use "timezone-based" but HuntSlots calls it "regional"
const JOB_REMOTE_TO_HUNT: Record<string, string> = {
	worldwide: "worldwide",
	"timezone-based": "regional",
	regional: "regional",
};

/** Serializes a single (v1) job signal — retained for backward-compat and the
 * round-trip tests. New writes use the rolling `pushHuntInterestEntry` (v2). */
export function serializeHuntInterest(signal: HuntInterestSignal): string {
	return JSON.stringify(signal);
}

// --- Rolling interest list (v2) --------------------------------------------
// The cookie graduated from a single last-viewed job (v1) to a rolling list of
// the most recently viewed jobs (v2). Last-write-wins discarded the browse
// *pattern* — the strongest intent signal a guest emits — so we now keep the
// last MAX_HUNT_INTEREST_JOBS distinct jobs and aggregate their skills/industry
// into the feed personalization scorer (see `aggregateHuntInterest`). v1 cookies
// already in the wild parse as a one-element list, so nothing is lost on upgrade.

export const MAX_HUNT_INTEREST_JOBS = 6;
/** Per-job skill cap kept in the cookie (the aggregate caps the union separately). */
export const HUNT_INTEREST_SKILLS_PER_JOB = 8;
/** Aggregate skill cap fed to the scorer — mirrors HuntSlots.topSkills (16). */
export const HUNT_INTEREST_MAX_AGGREGATE_SKILLS = 16;
const HUNT_INTEREST_MAX_AGGREGATE_INDUSTRIES = 5;

/** Reverse of JOB_REMOTE_TO_HUNT: Hunt pref → feed remoteType vocabulary, used
 * when folding the interest cookie into `UserSignals.preferredRemoteTypes`.
 * "onsite-okay" has no remote-feed equivalent and is intentionally unmapped. */
const HUNT_REMOTE_TO_FEED: Record<string, string> = {
	worldwide: "worldwide",
	regional: "regional",
};

/** One viewed-job entry in the rolling interest list. */
export type HuntInterestEntry = {
	/** Job id — present on v2 writes; used to dedupe repeat views. Optional so
	 * legacy v1 cookies (which had no id) still parse. */
	id?: string;
	skills: string[];
	remote: string | null;
	industry: string | null;
	ts?: number;
};

export type HuntInterestList = {
	v: 2;
	/** Newest-first, deduped by id, capped at MAX_HUNT_INTEREST_JOBS. */
	jobs: HuntInterestEntry[];
};

/** Aggregated, scorer-ready view of the rolling list. */
export type HuntInterestAggregate = {
	skills: string[];
	industries: string[];
	remoteType: string | null;
};

/** Folds, trims, and case-dedupes a raw skills array (legacy cap of 10). */
function sanitizeSkills(value: unknown): string[] {
	if (!Array.isArray(value)) return [];
	return dedupeFoldCase(
		value
			.filter((s): s is string => typeof s === "string" && s.length > 0)
			.map((s) => s.slice(0, 40).trim())
			.filter(Boolean),
	).slice(0, 10);
}

/** Validates one raw object into a HuntInterestEntry, or null when it carries no
 * usable signal (no skills). Shared by both the v1 and v2 parse paths. */
function normalizeHuntEntry(value: unknown): HuntInterestEntry | null {
	if (typeof value !== "object" || value === null) return null;
	const p = value as Record<string, unknown>;
	const skills = sanitizeSkills(p.skills);
	if (skills.length === 0) return null;
	const remote =
		typeof p.remote === "string" && VALID_HUNT_REMOTE.has(p.remote)
			? p.remote
			: null;
	const industry =
		typeof p.industry === "string" && p.industry.length > 0
			? p.industry.slice(0, 60)
			: null;
	const ts =
		typeof p.ts === "number" && Number.isFinite(p.ts) && p.ts > 0
			? p.ts
			: undefined;
	const id =
		typeof p.id === "string" && p.id.length > 0 ? p.id.slice(0, 64) : undefined;
	return {
		...(id ? { id } : {}),
		skills,
		remote,
		industry,
		...(ts ? { ts } : {}),
	};
}

/**
 * Parses the cookie into a normalized rolling list. Accepts both the v2 list
 * shape `{v:2, jobs:[…]}` and the legacy v1 single-object shape `{v:1, …}`
 * (mapped to a one-element list). Malformed / unknown-version values yield an
 * empty list. Entries are validated, deduped by id (newest wins), and capped.
 */
export function parseHuntInterestList(
	raw: string | null | undefined,
): HuntInterestList {
	if (!raw) return { v: 2, jobs: [] };
	let parsed: unknown;
	try {
		parsed = JSON.parse(decodeURIComponent(raw));
	} catch {
		return { v: 2, jobs: [] };
	}
	if (typeof parsed !== "object" || parsed === null) return { v: 2, jobs: [] };
	const obj = parsed as Record<string, unknown>;

	let entries: HuntInterestEntry[];
	if (obj.v === 2 && Array.isArray(obj.jobs)) {
		entries = obj.jobs
			.map(normalizeHuntEntry)
			.filter((e): e is HuntInterestEntry => e !== null);
	} else if (obj.v === 1) {
		const single = normalizeHuntEntry(obj);
		entries = single ? [single] : [];
	} else {
		return { v: 2, jobs: [] };
	}

	// Dedupe by id (newest wins — list is already newest-first), cap.
	const seen = new Set<string>();
	const deduped: HuntInterestEntry[] = [];
	for (const e of entries) {
		if (e.id) {
			if (seen.has(e.id)) continue;
			seen.add(e.id);
		}
		deduped.push(e);
		if (deduped.length >= MAX_HUNT_INTEREST_JOBS) break;
	}
	return { v: 2, jobs: deduped };
}

/**
 * Backward-compatible accessor: returns the most-recently-viewed job as a v1
 * `HuntInterestSignal`. The Hunt cold-start reader (startHuntSession) consumes
 * this unchanged — it only ever needed the latest view's skills/remote/industry
 * plus the freshness `ts`. Returns null when the list is empty.
 */
export function parseHuntInterestCookie(
	raw: string | null | undefined,
): HuntInterestSignal | null {
	const { jobs } = parseHuntInterestList(raw);
	const latest = jobs[0];
	if (!latest || latest.skills.length === 0) return null;
	return {
		v: 1,
		skills: latest.skills,
		remote: latest.remote,
		industry: latest.industry,
		...(latest.ts ? { ts: latest.ts } : {}),
	};
}

/**
 * Prepends a freshly-viewed job onto the rolling list and serializes it back to
 * a cookie value. Deduped by id (a repeat view moves the job to the front),
 * newest-first, capped. Skills are trimmed to HUNT_INTEREST_SKILLS_PER_JOB.
 * Returns null when the entry has no usable skills (caller leaves cookie as-is).
 */
export function pushHuntInterestEntry(
	currentRaw: string | null | undefined,
	entry: HuntInterestEntry,
): string | null {
	const skills = sanitizeSkills(entry.skills).slice(
		0,
		HUNT_INTEREST_SKILLS_PER_JOB,
	);
	if (skills.length === 0) return null;
	const normalized: HuntInterestEntry = {
		...(entry.id ? { id: entry.id.slice(0, 64) } : {}),
		skills,
		remote:
			entry.remote && VALID_HUNT_REMOTE.has(entry.remote) ? entry.remote : null,
		industry: entry.industry ? entry.industry.slice(0, 60) : null,
		ts: entry.ts && entry.ts > 0 ? entry.ts : Date.now(),
	};
	const { jobs } = parseHuntInterestList(currentRaw);
	const rest = normalized.id
		? jobs.filter((j) => j.id !== normalized.id)
		: jobs;
	const next: HuntInterestList = {
		v: 2,
		jobs: [normalized, ...rest].slice(0, MAX_HUNT_INTEREST_JOBS),
	};
	return JSON.stringify(next);
}

/**
 * Folds the rolling interest list into the scorer-ready aggregate: the union of
 * skills across recently-viewed jobs (newest-first, capped), the distinct
 * industries seen, and the most-recent remote preference (mapped to the feed's
 * remoteType vocabulary). This is what carries a guest's job-detail browsing
 * into the personalized feed ranking — not just the Hunt greeting.
 */
export function aggregateHuntInterest(
	list: HuntInterestList,
): HuntInterestAggregate {
	const skills: string[] = [];
	const seenSkills = new Set<string>();
	const industries: string[] = [];
	const seenIndustries = new Set<string>();
	let remoteType: string | null = null;
	for (const job of list.jobs) {
		for (const s of job.skills) {
			const key = s.toLowerCase();
			if (!seenSkills.has(key)) {
				seenSkills.add(key);
				skills.push(s);
			}
		}
		if (job.industry) {
			const key = job.industry.toLowerCase();
			if (!seenIndustries.has(key)) {
				seenIndustries.add(key);
				industries.push(job.industry);
			}
		}
		// Newest-first: the first entry with a mappable remote pref wins.
		if (remoteType === null && job.remote) {
			remoteType = HUNT_REMOTE_TO_FEED[job.remote] ?? null;
		}
	}
	return {
		skills: skills.slice(0, HUNT_INTEREST_MAX_AGGREGATE_SKILLS),
		industries: industries.slice(0, HUNT_INTEREST_MAX_AGGREGATE_INDUSTRIES),
		remoteType,
	};
}

/**
 * Converts a job's remoteType string into the HuntSlots remotePref value.
 * Returns null if the remoteType is unknown or absent.
 */
export function jobRemoteToHuntPref(
	remoteType: string | null | undefined,
): string | null {
	if (!remoteType) return null;
	return JOB_REMOTE_TO_HUNT[remoteType] ?? null;
}

// ---------------------------------------------------------------------------
// Recently-viewed jobs cookie — written client-side on every job detail view,
// read client-side to render the "Recently viewed" rail on the feed + homepage.
// Stores just enough to render + link a card (id, title, company); the heavier
// intent signal (skills/industry) lives in hunt_interest_v1, read server-side.
// ---------------------------------------------------------------------------

export const MAX_RECENT_JOBS = 6;
export const RECENT_JOBS_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 days
const RECENT_JOB_TITLE_MAX = 90;
const RECENT_JOB_COMPANY_MAX = 60;

export type RecentJob = {
	id: string;
	title: string;
	company: string | null;
	ts: number;
};

export type RecentJobsList = {
	v: 1;
	jobs: RecentJob[];
};

function normalizeRecentJob(value: unknown): RecentJob | null {
	if (typeof value !== "object" || value === null) return null;
	const p = value as Record<string, unknown>;
	const id =
		typeof p.id === "string" && p.id.length > 0 ? p.id.slice(0, 64) : null;
	const title =
		typeof p.title === "string" && p.title.trim().length > 0
			? p.title.trim().slice(0, RECENT_JOB_TITLE_MAX)
			: null;
	if (!id || !title) return null;
	const company =
		typeof p.company === "string" && p.company.trim().length > 0
			? p.company.trim().slice(0, RECENT_JOB_COMPANY_MAX)
			: null;
	const ts =
		typeof p.ts === "number" && Number.isFinite(p.ts) && p.ts > 0
			? p.ts
			: Date.now();
	return { id, title, company, ts };
}

/** Parses the recent-jobs cookie into a validated, deduped, capped list. */
export function parseRecentJobsCookie(
	raw: string | null | undefined,
): RecentJobsList {
	if (!raw) return { v: 1, jobs: [] };
	let parsed: unknown;
	try {
		parsed = JSON.parse(decodeURIComponent(raw));
	} catch {
		return { v: 1, jobs: [] };
	}
	if (
		typeof parsed !== "object" ||
		parsed === null ||
		(parsed as { v?: unknown }).v !== 1 ||
		!Array.isArray((parsed as { jobs?: unknown }).jobs)
	) {
		return { v: 1, jobs: [] };
	}
	const entries = (parsed as { jobs: unknown[] }).jobs
		.map(normalizeRecentJob)
		.filter((j): j is RecentJob => j !== null);
	const seen = new Set<string>();
	const deduped: RecentJob[] = [];
	for (const j of entries) {
		if (seen.has(j.id)) continue;
		seen.add(j.id);
		deduped.push(j);
		if (deduped.length >= MAX_RECENT_JOBS) break;
	}
	return { v: 1, jobs: deduped };
}

/**
 * Prepends a viewed job onto the recent-jobs list and serializes it back to a
 * cookie value. Deduped by id (a repeat view moves it to the front),
 * newest-first, capped. Returns null when the job is unusable (no id/title).
 */
export function pushRecentJob(
	currentRaw: string | null | undefined,
	job: { id: string; title: string; company: string | null },
): string | null {
	const normalized = normalizeRecentJob({ ...job, ts: Date.now() });
	if (!normalized) return null;
	const { jobs } = parseRecentJobsCookie(currentRaw);
	const next: RecentJobsList = {
		v: 1,
		jobs: [normalized, ...jobs.filter((j) => j.id !== normalized.id)].slice(
			0,
			MAX_RECENT_JOBS,
		),
	};
	return JSON.stringify(next);
}
